import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import { usePostHog } from 'posthog-js/react';
import { googleAdsAccountAtom, transitioningCampaignIdAtom } from '@/store';
import { useCampaignAllStateApproveMutationOptions } from '@/lib/query-options';
import { useRefetchCampaign } from '@/hooks/useRefetchCampaign';
import { useCampaignStateTransition } from '@/hooks/useCampaignStateTransition';
import { CampaignData, CAMPAIGN_STATE, APPROVAL_STATE } from '@/lib/types';

interface UseCampaignActionsProps {
  campaign: CampaignData;
  onCopy?: () => void;
  onDelete?: () => void;
}

export const useCampaignActions = ({ campaign, onCopy, onDelete }: UseCampaignActionsProps) => {

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const posthog = usePostHog();
  const selectedGoogleAdsAccount = useAtomValue(googleAdsAccountAtom);
  const setTransitioningCampaignId = useSetAtom(transitioningCampaignIdAtom);
  const [isRefetching, setIsRefetching] = useState(false);

  // Mutations
  const campaignAllStateApproveMutation = useCampaignAllStateApproveMutationOptions(campaign?.slug ?? '');
  const refetchCampaign = useRefetchCampaign(campaign?.slug ?? '');
  // Fire-and-poll like Go Live/Deploy: dispatch resolves on accept (202), then isPending
  // tracks the state-update task to completion. The old direct mutation resolved at
  // accept, so spinners dropped while the task was still running and the toolbar showed
  // stale/contradictory states (#5669).
  const {
    dispatch: dispatchStateChange,
    isPending: isTransitionPending,
    pendingTarget,
    pendingSource,
  } = useCampaignStateTransition(campaign?.id ?? 0, campaign?.state as CAMPAIGN_STATE | undefined, 'pause_toggle');

  // Helper function for Google Ads management
  const handleManageCampaignWithGoogle = async (campaignState: CAMPAIGN_STATE) => {
    if (!selectedGoogleAdsAccount) return;

    try {
      await dispatchStateChange(campaignState);
      setIsRefetching(true);
      await Promise.all([
        refetchCampaign(),
        queryClient.invalidateQueries({ queryKey: ['campaigns'] }),
      ]);
    } catch {
      // Dispatch failures already toast inside useCampaignStateTransition.
    } finally {
      setIsRefetching(false);
    }
  };

  // Action handlers
  const handleViewPerformance = () => {
    window.location.href = `/performance/${campaign.slug}`;
  };

  const handleSyncCampaign = async () => {
    await handleManageCampaignWithGoogle(CAMPAIGN_STATE.LIVE);
  };

  const handleTogglePause = async () => {
    const newState = campaign?.state === CAMPAIGN_STATE.DRAFT || campaign?.state === CAMPAIGN_STATE.PAUSED
      ? CAMPAIGN_STATE.LIVE
      : CAMPAIGN_STATE.PAUSED;
    setTransitioningCampaignId(campaign?.id ?? null);
    try {
      await handleManageCampaignWithGoogle(newState);
    } finally {
      setTransitioningCampaignId(null);
    }
  };

  const handleViewInGoogleAds = () => {
    if (campaign?.googleads_campaign?.googleads_campaign_id && campaign?.googleads_account?.googleads_account_id) {
      posthog?.capture('campaign_viewed_in_google_ads', { campaign_id: campaign?.id });
      window.open(
        `https://ads.google.com/aw/campaigns?campaignId=${campaign.googleads_campaign.googleads_campaign_id}&customerId=${campaign.googleads_account.googleads_account_id}`,
        '_blank',
        'noopener,noreferrer'
      );
    }
  };

  const handleManageCampaign = () => {
    navigate({ to: `/campaigns/google/${campaign.slug}/settings` });
  };

  const handleCopyCampaign = () => {
    posthog?.capture('campaign_copy_clicked', { campaign_id: campaign?.id, campaign_slug: campaign?.slug });
    if (onCopy) {
      onCopy();
    }
  };

  const handleDeleteCampaign = () => {
    posthog?.capture('campaign_delete_clicked', { campaign_id: campaign?.id, campaign_slug: campaign?.slug });
    if (onDelete) {
      onDelete();
    }
  };

  // Check if actions are enabled
  const isBudgetApproved = campaign?.budget_state?.toLowerCase() === APPROVAL_STATE.APPROVED.toLowerCase();
  const hasGoogleAdsConnection = campaign?.googleads_campaign?.googleads_campaign_id && campaign?.googleads_account?.googleads_account_id;
  
  const isPauseResumeText = campaign?.state === CAMPAIGN_STATE.DRAFT || campaign?.state === CAMPAIGN_STATE.PAUSED
    ? 'Resume'
    : 'Pause';

  // Only transitions this toggle dispatched (attribution via pendingSource, #5663) —
  // a Go Live launch in flight must not read as pausing/resuming.
  const isTogglePending = isTransitionPending && pendingSource === 'pause_toggle';
  const toggleTransition: 'pausing' | 'resuming' | null = isTogglePending
    ? (pendingTarget === CAMPAIGN_STATE.PAUSED ? 'pausing' : 'resuming')
    : null;

  return {
    actions: {
      handleViewPerformance,
      handleSyncCampaign,
      handleTogglePause,
      handleViewInGoogleAds,
      handleManageCampaign,
      handleCopyCampaign,
      handleDeleteCampaign,
    },
    states: {
      isBudgetApproved,
      hasGoogleAdsConnection,
      isPauseResumeText,
      isLoading: isTogglePending || campaignAllStateApproveMutation.isPending || isRefetching,
      isTogglePending,
      toggleTransition,
    },
  };
};